import { FormEvent, useEffect, useMemo, useState } from "react";
import { CalendarClock, Factory, ShieldCheck, Tags, Truck } from "lucide-react";
import api from "../../api";
import { useApi } from "../../hooks/useApi";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage, getFieldErrors } from "../../utils/apiError";
import {
    Customer,
    CustomerProduct,
    DeliveryStatus,
    DeviceType,
    PaymentStatus,
    Product,
    ProductStatus,
    ProductType,
    SubscriptionPlan,
} from "../../types";
import {
    DELIVERY_STATUSES,
    PAYMENT_STATUSES,
    PRODUCT_STATUSES,
    PRODUCT_TYPES,
    ZATCA_PHASES,
    labelOf,
} from "../../constants/enums";
import { addPlanDuration, formatDate, formatDuration, toInputDate, todayInput } from "../../utils/format";
import Modal from "../../components/ui/Modal";
import Button from "../../components/ui/Button";
import { Checkbox, Input, Segmented, Select } from "../../components/ui/FormField";

interface Props {
    open: boolean;
    /** null = create */
    item: CustomerProduct | null;
    /** When opened from a customer's page, the customer is fixed */
    fixedCustomer?: { id: number; name: string; customerId: number } | null;
    onClose: () => void;
    onSaved: () => void;
}

interface FormState {
    customerId: string;
    type: ProductType | "";
    productId: string;
    amount: string;
    paymentStatus: PaymentStatus;
    // hardware
    deliveryDate: string;
    deliveryStatus: DeliveryStatus;
    // software
    deviceTypeId: string;
    subscriptionPlanId: string;
    renewalAmount: string;
    startDate: string;
    renewalManual: boolean;
    renewalDate: string;
    status: ProductStatus;
}

const emptyForm = (): FormState => ({
    customerId: "",
    type: "",
    productId: "",
    amount: "",
    paymentStatus: "NOT_PAID",
    deliveryDate: "",
    deliveryStatus: "PENDING",
    deviceTypeId: "",
    subscriptionPlanId: "",
    renewalAmount: "",
    startDate: todayInput(),
    renewalManual: false,
    renewalDate: "",
    status: "ACTIVE",
});

const fromItem = (r: CustomerProduct): FormState => ({
    customerId: String(r.customerId),
    type: r.product.type,
    productId: String(r.productId),
    amount: String(Number(r.amount)),
    paymentStatus: r.paymentStatus,
    deliveryDate: toInputDate(r.deliveryDate),
    deliveryStatus: r.deliveryStatus ?? "PENDING",
    deviceTypeId: r.deviceTypeId ? String(r.deviceTypeId) : "",
    subscriptionPlanId: r.subscriptionPlanId ? String(r.subscriptionPlanId) : "",
    renewalAmount: r.renewalAmount ? String(Number(r.renewalAmount)) : "",
    startDate: toInputDate(r.startDate) || todayInput(),
    renewalManual: false,
    renewalDate: toInputDate(r.renewalDate),
    status: r.status,
});

export default function CustomerProductFormModal({ open, item, fixedCustomer, onClose, onSaved }: Props) {
    const toast = useToast();
    const isEdit = !!item;
    const needCustomerList = open && !isEdit && !fixedCustomer;

    const { data: customers } = useApi<Customer[]>(needCustomerList ? "/customers" : null);
    const { data: products } = useApi<Product[]>(open ? "/product" : null);
    const { data: deviceTypes } = useApi<DeviceType[]>(open ? "/device-type" : null);
    const { data: plans } = useApi<SubscriptionPlan[]>(open ? "/subscription-plan" : null);

    const [form, setForm] = useState<FormState>(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(item ? fromItem(item) : { ...emptyForm(), customerId: fixedCustomer ? String(fixedCustomer.id) : "" });
    }, [open, item, fixedCustomer]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: "" }));
    };

    const productOptions = useMemo(
        () =>
            (products ?? [])
                .filter((p) => p.type === form.type)
                .map((p) => ({ value: String(p.id), label: `${p.name} — ${p.category?.name ?? ""}` })),
        [products, form.type]
    );

    const selectedProduct = useMemo(
        () => (products ?? []).find((p) => String(p.id) === form.productId) ?? null,
        [products, form.productId]
    );

    const selectedPlan = useMemo(
        () => (plans ?? []).find((p) => String(p.id) === form.subscriptionPlanId) ?? null,
        [plans, form.subscriptionPlanId]
    );

    // What the server will set if we don't send renewalDate
    const autoRenewal = useMemo(() => {
        if (!selectedPlan || !form.startDate) return "";
        if (item && item.subscriptionPlanId === selectedPlan.id && toInputDate(item.startDate) === form.startDate) {
            return toInputDate(item.renewalDate); // unchanged -> server keeps the current date
        }
        return addPlanDuration(form.startDate, selectedPlan.duration, selectedPlan.durationType);
    }, [selectedPlan, form.startDate, item]);

    const changeType = (type: ProductType) => {
        if (type === form.type) return;
        setForm((f) => ({ ...f, type, productId: "" }));
        setErrors({});
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const errs: Record<string, string> = {};
        if (!form.customerId) errs.customerId = "Select a customer";
        if (!form.type) errs.type = "Choose hardware or software";
        if (!form.productId) errs.productId = "Select a product";
        if (form.type === "SOFTWARE" && !form.subscriptionPlanId) errs.subscriptionPlanId = "Select a plan";
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }

        const body: Record<string, unknown> = {
            productId: Number(form.productId),
            amount: form.amount,
            paymentStatus: form.paymentStatus,
        };

        if (!isEdit) body.customerId = Number(form.customerId);

        if (form.type === "HARDWARE") {
            body.deliveryDate = form.deliveryDate || null;
            body.deliveryStatus = form.deliveryStatus;
        } else {
            body.subscriptionPlanId = Number(form.subscriptionPlanId);
            body.deviceTypeId = form.deviceTypeId ? Number(form.deviceTypeId) : null;
            body.renewalAmount = form.renewalAmount || null;
            body.status = form.status;

            // Only send startDate when new/changed, otherwise the server recalculates renewal
            if (!isEdit || form.startDate !== toInputDate(item!.startDate)) body.startDate = form.startDate;
            if (form.renewalManual && form.renewalDate) body.renewalDate = form.renewalDate;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/customer-product/${item!.id}`, body);
                toast.success("Customer product updated");
            } else {
                await api.post("/customer-product", body);
                toast.success("Product assigned to customer");
            }
            onSaved();
        } catch (err) {
            setErrors(getFieldErrors(err));
            toast.error(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const customerLabel = fixedCustomer ?? (item ? item.customer : null);

    return (
        <Modal
            open={open}
            onClose={onClose}
            locked={saving}
            size="lg"
            title={isEdit ? "Edit customer product" : "Assign product"}
            subtitle={customerLabel ? `${customerLabel.name} · #${customerLabel.customerId}` : "Choose a customer and a product."}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="submit" form="cp-form" loading={saving}>
                        {isEdit ? "Save changes" : "Assign product"}
                    </Button>
                </>
            }
        >
            <form id="cp-form" onSubmit={handleSubmit} className="space-y-6">
                {/* Customer + type + product */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {!customerLabel && (
                        <Select
                            wrapperClassName="sm:col-span-2"
                            label="Customer"
                            required
                            value={form.customerId}
                            onChange={(e) => set("customerId", e.target.value)}
                            options={(customers ?? []).map((c) => ({ value: String(c.id), label: `${c.name} (#${c.customerId})` }))}
                            placeholder={customers ? "Select customer" : "Loading customers…"}
                            error={errors.customerId}
                        />
                    )}

                    <Segmented
                        label="Type"
                        required
                        value={form.type}
                        options={PRODUCT_TYPES}
                        onChange={changeType}
                        disabled={isEdit}
                        error={errors.type}
                    />

                    <Select
                        label="Product"
                        required
                        value={form.productId}
                        onChange={(e) => set("productId", e.target.value)}
                        options={productOptions}
                        disabled={!form.type}
                        placeholder={!form.type ? "Choose a type first" : productOptions.length ? "Select product" : "No products of this type"}
                        error={errors.productId}
                    />
                </div>

                {/* Read-only info that comes from the product */}
                {selectedProduct && (
                    <div className="flex flex-wrap gap-2">
                        <InfoChip icon={Tags} label="Category" value={selectedProduct.category?.name ?? "—"} />
                        {selectedProduct.type === "HARDWARE" ? (
                            <InfoChip icon={Factory} label="Manufacturer" value={selectedProduct.manufacturer?.name ?? "—"} />
                        ) : (
                            <InfoChip
                                icon={ShieldCheck}
                                label="ZATCA"
                                value={selectedProduct.zatcaPhase ? labelOf(ZATCA_PHASES, selectedProduct.zatcaPhase) : "n/a"}
                            />
                        )}
                    </div>
                )}

                {form.type && (
                    <>
                        {/* Pricing (both) */}
                        <Section title="Billing">
                            <Input
                                label="Amount (SAR)"
                                required
                                type="number"
                                min={0}
                                step="0.01"
                                inputMode="decimal"
                                value={form.amount}
                                onChange={(e) => set("amount", e.target.value)}
                                error={errors.amount}
                                placeholder={form.type === "HARDWARE" ? "5000" : "1000"}
                            />
                            {form.type === "SOFTWARE" && (
                                <Input
                                    label="Renewal amount (SAR)"
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    inputMode="decimal"
                                    value={form.renewalAmount}
                                    onChange={(e) => set("renewalAmount", e.target.value)}
                                    error={errors.renewalAmount}
                                    placeholder="500"
                                />
                            )}
                            <Select
                                label="Payment status"
                                required
                                value={form.paymentStatus}
                                onChange={(e) => set("paymentStatus", e.target.value as PaymentStatus)}
                                options={PAYMENT_STATUSES}
                                error={errors.paymentStatus}
                            />
                        </Section>

                        {form.type === "HARDWARE" ? (
                            <Section title="Delivery" icon={Truck}>
                                <Input
                                    label="Delivery date"
                                    type="date"
                                    value={form.deliveryDate}
                                    onChange={(e) => set("deliveryDate", e.target.value)}
                                    error={errors.deliveryDate}
                                />
                                <Select
                                    label="Delivery status"
                                    required
                                    value={form.deliveryStatus}
                                    onChange={(e) => set("deliveryStatus", e.target.value as DeliveryStatus)}
                                    options={DELIVERY_STATUSES}
                                    error={errors.deliveryStatus}
                                />
                            </Section>
                        ) : (
                            <Section title="Subscription" icon={CalendarClock}>
                                <Select
                                    label="Subscription plan"
                                    required
                                    value={form.subscriptionPlanId}
                                    onChange={(e) => set("subscriptionPlanId", e.target.value)}
                                    options={(plans ?? []).map((p) => ({
                                        value: String(p.id),
                                        label: `${p.name} (${formatDuration(p.duration, p.durationType)})`,
                                    }))}
                                    placeholder="Select plan"
                                    error={errors.subscriptionPlanId}
                                />
                                <Select
                                    label="Device type"
                                    value={form.deviceTypeId}
                                    onChange={(e) => set("deviceTypeId", e.target.value)}
                                    options={(deviceTypes ?? []).map((d) => ({ value: String(d.id), label: d.name }))}
                                    placeholder="Any / not specified"
                                    error={errors.deviceTypeId}
                                />
                                <Input
                                    label="Start date"
                                    required
                                    type="date"
                                    value={form.startDate}
                                    onChange={(e) => set("startDate", e.target.value)}
                                    error={errors.startDate}
                                />
                                <Select
                                    label="Status"
                                    required
                                    value={form.status}
                                    onChange={(e) => set("status", e.target.value as ProductStatus)}
                                    options={PRODUCT_STATUSES}
                                    error={errors.status}
                                />

                                <div className="sm:col-span-2 space-y-3">
                                    <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-[#8b5cf6]/5 border border-[#8b5cf6]/20">
                                        <div>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">Renewal date</p>
                                            <p className="text-lg font-bold text-white mt-0.5">
                                                {form.renewalManual && form.renewalDate
                                                    ? formatDate(form.renewalDate)
                                                    : autoRenewal
                                                      ? formatDate(autoRenewal)
                                                      : "—"}
                                            </p>
                                        </div>
                                        <span className="text-xs text-slate-400 text-right">
                                            {form.renewalManual ? "Set manually" : selectedPlan ? "Start date + plan length" : "Pick a plan"}
                                        </span>
                                    </div>
                                    <Checkbox
                                        label="Set renewal date manually"
                                        checked={form.renewalManual}
                                        onChange={(v) => {
                                            set("renewalManual", v);
                                            if (v && !form.renewalDate) set("renewalDate", autoRenewal);
                                        }}
                                    />
                                    {form.renewalManual && (
                                        <Input
                                            label="Renewal date"
                                            type="date"
                                            required
                                            min={form.startDate}
                                            value={form.renewalDate}
                                            onChange={(e) => set("renewalDate", e.target.value)}
                                            error={errors.renewalDate}
                                        />
                                    )}
                                </div>
                            </Section>
                        )}
                    </>
                )}
            </form>
        </Modal>
    );
}

function Section({ title, icon: Icon, children }: { title: string; icon?: React.ElementType; children: React.ReactNode }) {
    return (
        <div>
            <p className="flex items-center gap-2 text-xs font-bold text-[#a78bfa] uppercase tracking-[0.2em] mb-3">
                {Icon && <Icon size={14} />}
                {title}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function InfoChip({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
    return (
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800/40 border border-slate-800 text-xs">
            <Icon size={13} className="text-[#8b5cf6]" />
            <span className="text-slate-500">{label}</span>
            <span className="text-slate-200 font-semibold">{value}</span>
        </span>
    );
}